import { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { MapPin, Save, Truck, Camera, Navigation } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const emptyOffer = () => ({
  id: `offer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: '',
  description: '',
  trigger_quantity: 5,
  reward_quantity: 1,
  trigger_dish_ids: [],
  reward_dish_ids: [],
  trigger_item_quantities: {},
  reward_item_quantities: {},
  active: true,
});

const createPrinterDraft = () => ({
  id: `printer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: 'Epson TM-M30II',
  type: 'bluetooth',
  connection: {
    host: '',
    port: 9100,
    bluetooth_address: '',
  },
  secret: '',
  auto_print_on_confirm: true,
  active: false,
  status: 'offline',
  last_seen_at: '',
});

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [settings, setSettings] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsForm, setCredentialsForm] = useState({
    current_password: '',
    new_email: '',
    new_password: '',
    confirm_password: '',
  });
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const circleRef = useRef(null);
  const restaurantLat = settings?.restaurant_lat;
  const restaurantLng = settings?.restaurant_lng;
  const maxDeliveryDistanceKm = settings?.max_delivery_distance_km;

  useEffect(() => {
    Promise.all([
      api.get('/settings'),
      api.get('/dishes'),
      api.get('/categories'),
    ]).then(([settingsResponse, dishesResponse, categoriesResponse]) => {
      setSettings(settingsResponse.data);
      setDishes(dishesResponse.data || []);
      setCategories(categoriesResponse.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setCredentialsForm(prev => ({
      ...prev,
      new_email: user?.email || '',
    }));
  }, [user]);

  useEffect(() => {
    if (restaurantLat == null || restaurantLng == null || !mapRef.current) return;
    if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }

    const map = L.map(mapRef.current).setView([restaurantLat, restaurantLng], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    const icon = L.divIcon({ html: '<div style="background:#5B6B3F;width:16px;height:16px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>', className: '', iconSize: [16, 16], iconAnchor: [8, 8] });
    L.marker([restaurantLat, restaurantLng], { icon }).addTo(map);

    circleRef.current = L.circle([restaurantLat, restaurantLng], {
      radius: (maxDeliveryDistanceKm || 5) * 1000,
      color: '#5B6B3F', fillColor: '#5B6B3F', fillOpacity: 0.08, weight: 2
    }).addTo(map);

    map.fitBounds(circleRef.current.getBounds());
    mapInstance.current = map;

    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [restaurantLat, restaurantLng, maxDeliveryDistanceKm]);

  const updateOffer = (index, patch) => {
    setSettings(prev => ({
      ...prev,
      offers: (prev.offers || []).map((offer, offerIndex) => (
        offerIndex === index ? { ...offer, ...patch } : offer
      )),
    }));
  };

  const toggleOfferDish = (index, field, dishId) => {
    setSettings(prev => ({
      ...prev,
      offers: (prev.offers || []).map((offer, offerIndex) => {
        if (offerIndex !== index) return offer;
        const current = offer[field] || [];
        const next = current.includes(dishId)
          ? current.filter(id => id !== dishId)
          : [...current, dishId];
        const quantityField = field === 'trigger_dish_ids' ? 'trigger_item_quantities' : 'reward_item_quantities';
        const currentQuantities = offer[quantityField] || {};
        const nextQuantities = { ...currentQuantities };
        if (next.includes(dishId)) {
          if (!nextQuantities[dishId]) nextQuantities[dishId] = 1;
        } else {
          delete nextQuantities[dishId];
        }
        return { ...offer, [field]: next, [quantityField]: nextQuantities };
      }),
    }));
  };

  const setOfferDishQuantity = (index, quantityField, idsField, dishId, nextQuantity) => {
    setSettings(prev => ({
      ...prev,
      offers: (prev.offers || []).map((offer, offerIndex) => {
        if (offerIndex !== index) return offer;
        const safeQuantity = Math.max(0, Number(nextQuantity) || 0);
        const currentQuantities = { ...(offer[quantityField] || {}) };
        let nextIds = [...(offer[idsField] || [])];
        if (safeQuantity <= 0) {
          delete currentQuantities[dishId];
          nextIds = nextIds.filter(id => id !== dishId);
        } else {
          currentQuantities[dishId] = safeQuantity;
          if (!nextIds.includes(dishId)) nextIds.push(dishId);
        }
        return {
          ...offer,
          [quantityField]: currentQuantities,
          [idsField]: nextIds,
        };
      }),
    }));
  };

  const updatePrinter = (printerIndex, patch) => {
    setSettings(prev => ({
      ...prev,
      printers: (prev.printers || []).map((printer, index) => index === printerIndex ? { ...printer, ...patch } : printer),
    }));
  };

  const updatePrinterConnection = (printerIndex, field, value) => {
    setSettings(prev => ({
      ...prev,
      printers: (prev.printers || []).map((printer, index) => (
        index === printerIndex
          ? { ...printer, connection: { ...(printer.connection || {}), [field]: value } }
          : printer
      )),
    }));
  };

  const getDishLabel = (dishId) => {
    const dish = dishes.find(item => item.id === dishId);
    if (!dish) return '';
    const category = categories.find(item => item.id === dish.category_id);
    return `${dish.name}${category ? ` - ${category.name}` : ''}${dish.dish_type === 'formula' ? ' (Formule)' : ''}`;
  };

  const availableOfferItems = [...dishes]
    .filter(dish => dish.available !== false)
    .sort((a, b) => getDishLabel(a.id).localeCompare(getDishLabel(b.id), 'fr', { sensitivity: 'base' }));

  const getReservationSeatingMode = () => {
    const insideEnabled = settings?.reservation_inside_enabled !== false;
    const terraceEnabled = settings?.reservation_terrace_enabled !== false;
    if (insideEnabled && terraceEnabled) return 'both';
    if (insideEnabled) return 'inside';
    if (terraceEnabled) return 'terrace';
    return 'both';
  };

  const setReservationSeatingMode = (mode) => {
    if (mode === 'inside') {
      setSettings({
        ...settings,
        reservation_inside_enabled: true,
        reservation_terrace_enabled: false,
      });
      return;
    }

    if (mode === 'terrace') {
      setSettings({
        ...settings,
        reservation_inside_enabled: false,
        reservation_terrace_enabled: true,
      });
      return;
    }

    setSettings({
      ...settings,
      reservation_inside_enabled: true,
      reservation_terrace_enabled: true,
    });
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/settings', {
        ...settings,
        restaurant_lat: parseFloat(settings.restaurant_lat),
        restaurant_lng: parseFloat(settings.restaurant_lng),
        max_delivery_distance_km: parseFloat(settings.max_delivery_distance_km),
        delivery_fee: parseFloat(settings.delivery_fee),
        badge_max_distance_meters: parseFloat(settings.badge_max_distance_meters),
        badge_require_selfie: !!settings.badge_require_selfie,
        badge_require_geolocation: !!settings.badge_require_geolocation,
        payment_method_cash_enabled: !!settings.payment_method_cash_enabled,
        payment_method_card_enabled: !!settings.payment_method_card_enabled,
        payment_method_ticket_restaurant_enabled: !!settings.payment_method_ticket_restaurant_enabled,
        payment_method_other_enabled: !!settings.payment_method_other_enabled,
        payment_card_fee_percent: parseFloat(settings.payment_card_fee_percent),
        default_vat_rate: parseFloat(settings.default_vat_rate),
        reservation_max_guests: parseInt(settings.reservation_max_guests, 10),
        reservation_slot_minutes: parseInt(settings.reservation_slot_minutes, 10),
        reservation_terrace_enabled: !!settings.reservation_terrace_enabled,
        reservation_inside_enabled: !!settings.reservation_inside_enabled,
        delivery_enabled: !!settings.delivery_enabled,
        takeaway_enabled: !!settings.takeaway_enabled,
        dine_in_enabled: !!settings.dine_in_enabled,
        exceptional_closure_enabled: !!settings.exceptional_closure_enabled,
        exceptional_closure_allow_orders: !!settings.exceptional_closure_allow_orders,
        reservations_enabled: settings.reservations_enabled !== false,
        quotes_enabled: settings.quotes_enabled !== false,
        offers: (settings.offers || []).map(offer => ({
          ...offer,
          trigger_item_quantities: Object.fromEntries(
            Object.entries(offer.trigger_item_quantities || {})
              .map(([dishId, quantity]) => [dishId, Math.max(0, parseInt(quantity, 10) || 0)])
              .filter(([, quantity]) => quantity > 0)
          ),
          reward_item_quantities: Object.fromEntries(
            Object.entries(offer.reward_item_quantities || {})
              .map(([dishId, quantity]) => [dishId, Math.max(0, parseInt(quantity, 10) || 0)])
              .filter(([, quantity]) => quantity > 0)
          ),
          trigger_dish_ids: Object.entries(offer.trigger_item_quantities || {})
            .filter(([, quantity]) => (parseInt(quantity, 10) || 0) > 0)
            .map(([dishId]) => dishId),
          reward_dish_ids: Object.entries(offer.reward_item_quantities || {})
            .filter(([, quantity]) => (parseInt(quantity, 10) || 0) > 0)
            .map(([dishId]) => dishId),
          trigger_quantity: Object.values(offer.trigger_item_quantities || {}).reduce((sum, quantity) => sum + (parseInt(quantity, 10) || 0), 0) || 1,
          reward_quantity: Object.values(offer.reward_item_quantities || {}).reduce((sum, quantity) => sum + (parseInt(quantity, 10) || 0), 0) || 1,
          active: offer.active !== false,
        })),
        printers: settings.printers || [],
      });
      toast.success('Parametres enregistres');
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const saveCredentials = async () => {
    if (!credentialsForm.current_password) {
      toast.error('Saisissez le mot de passe actuel');
      return;
    }
    if (!credentialsForm.new_email.trim()) {
      toast.error("L'email admin est requis");
      return;
    }
    if (credentialsForm.new_password && credentialsForm.new_password !== credentialsForm.confirm_password) {
      toast.error('La confirmation du mot de passe ne correspond pas');
      return;
    }

    setCredentialsLoading(true);
    try {
      const { data } = await api.put('/auth/admin-credentials', {
        current_password: credentialsForm.current_password,
        new_email: credentialsForm.new_email,
        new_password: credentialsForm.new_password,
      });
      if (data?.token) {
        localStorage.setItem('auth_token', data.token);
      }
      await checkAuth();
      setCredentialsForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      toast.success('Identifiants admin mis a jour');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur de mise a jour admin');
    } finally {
      setCredentialsLoading(false);
    }
  };

  if (!settings) return <div className="text-[#7A7A75]">Chargement...</div>;

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-heading text-2xl font-bold text-[#2A2A28] mb-6">Parametres</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Settings */}
        <div className="bg-white rounded-xl border border-[#EBE8E0] p-5">
          <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4 flex items-center gap-2"><Truck className="w-5 h-5 text-[#5B6B3F]" /> Livraison</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">URL publique du site</Label>
              <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.public_site_url || ''} onChange={e => setSettings({...settings, public_site_url: e.target.value})} placeholder="https://www.letraiteurlibanais.fr" data-testid="settings-public-site-url" />
              <p className="text-xs text-[#7A7A75] mt-1">Utilisee pour les QR codes des tables afin que les clients ouvrent la bonne page sur leur telephone.</p>
            </div>
            <div>
              <Label className="text-sm">Adresse du restaurant</Label>
              <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.restaurant_address} onChange={e => setSettings({...settings, restaurant_address: e.target.value})} data-testid="settings-address" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#5B6B3F]">Distance max de livraison (km)</Label>
              <Input type="number" step="0.5" min="0.5" className="mt-1 rounded-xl border-[#EBE8E0] text-lg font-bold" value={settings.max_delivery_distance_km} onChange={e => setSettings({...settings, max_delivery_distance_km: e.target.value})} data-testid="settings-max-distance" />
            </div>
            <div>
              <Label className="text-sm">Frais de livraison (EUR)</Label>
              <Input type="number" step="0.5" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.delivery_fee} onChange={e => setSettings({...settings, delivery_fee: e.target.value})} />
            </div>
          </div>
        </div>

        {/* Badge Settings */}
        <div className="bg-white rounded-xl border border-[#EBE8E0] p-5">
          <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#5B6B3F]" /> Badgeage</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
              <div>
                <Label className="text-sm font-semibold text-[#2A2A28]">Geolocalisation obligatoire</Label>
                <p className="text-xs text-[#7A7A75] mt-1">Le pointage n'est possible qu'a proximite du restaurant</p>
              </div>
              <Switch
                checked={!!settings.badge_require_geolocation}
                onCheckedChange={checked => setSettings({ ...settings, badge_require_geolocation: checked })}
                data-testid="settings-badge-geo"
              />
            </div>
            <Label className="text-sm font-semibold text-[#5B6B3F]">Distance max pour badger (metres)</Label>
            <Input type="number" step="10" min="10" className="mt-1 rounded-xl border-[#EBE8E0] text-lg font-bold" value={settings.badge_max_distance_meters} onChange={e => setSettings({...settings, badge_max_distance_meters: e.target.value})} data-testid="settings-badge-distance" />
            <p className="text-xs text-[#7A7A75] mt-1">L'employe doit etre a moins de cette distance du restaurant pour pointer</p>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Compte administrateur</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Email admin</Label>
            <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={credentialsForm.new_email} onChange={e => setCredentialsForm({ ...credentialsForm, new_email: e.target.value })} data-testid="settings-admin-email" />
          </div>
          <div>
            <Label className="text-sm">Mot de passe actuel</Label>
            <Input type="password" className="mt-1 rounded-xl border-[#EBE8E0]" value={credentialsForm.current_password} onChange={e => setCredentialsForm({ ...credentialsForm, current_password: e.target.value })} data-testid="settings-admin-current-password" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Nouveau mot de passe</Label>
              <Input type="password" className="mt-1 rounded-xl border-[#EBE8E0]" value={credentialsForm.new_password} onChange={e => setCredentialsForm({ ...credentialsForm, new_password: e.target.value })} data-testid="settings-admin-new-password" />
            </div>
            <div>
              <Label className="text-sm">Confirmer le nouveau mot de passe</Label>
              <Input type="password" className="mt-1 rounded-xl border-[#EBE8E0]" value={credentialsForm.confirm_password} onChange={e => setCredentialsForm({ ...credentialsForm, confirm_password: e.target.value })} data-testid="settings-admin-confirm-password" />
            </div>
          </div>
          <Button onClick={saveCredentials} disabled={credentialsLoading} className="rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" data-testid="settings-admin-save">
            <Save className="w-4 h-4 mr-2" /> {credentialsLoading ? 'Mise a jour...' : 'Mettre a jour les acces admin'}
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Modes de paiement</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Argent liquide</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Autoriser le paiement en especes</p>
            </div>
            <Switch checked={!!settings.payment_method_cash_enabled} onCheckedChange={checked => setSettings({ ...settings, payment_method_cash_enabled: checked })} data-testid="settings-payment-cash" />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Carte bleue en ligne</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Desactivez si le terminal CB ne fonctionne pas. Integration preparee pour SumUp.</p>
            </div>
            <Switch checked={!!settings.payment_method_card_enabled} onCheckedChange={checked => setSettings({ ...settings, payment_method_card_enabled: checked })} data-testid="settings-payment-card" />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Ticket restaurant</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Autoriser ce mode de paiement</p>
            </div>
            <Switch checked={!!settings.payment_method_ticket_restaurant_enabled} onCheckedChange={checked => setSettings({ ...settings, payment_method_ticket_restaurant_enabled: checked })} data-testid="settings-payment-ticket" />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Carte bancaire a la reception</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Paiement par carte directement au restaurant</p>
            </div>
            <Switch checked={!!settings.payment_method_other_enabled} onCheckedChange={checked => setSettings({ ...settings, payment_method_other_enabled: checked })} data-testid="settings-payment-other" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-sm">Fournisseur CB</Label>
              <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.payment_card_provider || 'sumup'} onChange={e => setSettings({ ...settings, payment_card_provider: e.target.value })} />
            </div>
            <div>
              <Label className="text-sm">Frais de gestion CB (%)</Label>
              <Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.payment_card_fee_percent || 1.75} onChange={e => setSettings({ ...settings, payment_card_fee_percent: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Reservations</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm">Nombre maximum de personnes</Label>
            <Input type="number" min="1" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_max_guests || 30} onChange={e => setSettings({ ...settings, reservation_max_guests: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm">Derniere reservation</Label>
            <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_last_time || '21:00'} onChange={e => setSettings({ ...settings, reservation_last_time: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm">Debut reservations</Label>
            <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_hours_start || '11:30'} onChange={e => setSettings({ ...settings, reservation_hours_start: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm">Fin reservations</Label>
            <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_hours_end || '21:00'} onChange={e => setSettings({ ...settings, reservation_hours_end: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm">Pause apres-midi debut</Label>
            <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_midday_break_start || '15:30'} onChange={e => setSettings({ ...settings, reservation_midday_break_start: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm">Pause apres-midi fin</Label>
            <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_midday_break_end || '19:00'} onChange={e => setSettings({ ...settings, reservation_midday_break_end: e.target.value })} />
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div>
            <Label className="text-sm">Pas des creneaux (minutes)</Label>
            <Input type="number" min="15" step="15" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.reservation_slot_minutes || 30} onChange={e => setSettings({ ...settings, reservation_slot_minutes: e.target.value })} />
          </div>
          <div className="md:col-span-2 rounded-xl border border-[#EBE8E0] p-3">
            <Label className="text-sm">Zone de reservation disponible</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { value: 'inside', label: 'Interieur' },
                { value: 'terrace', label: 'Terrasse' },
                { value: 'both', label: 'Les deux' },
              ].map((option) => {
                const selected = getReservationSeatingMode() === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setReservationSeatingMode(option.value)}
                    className={`rounded-xl border px-4 py-3 text-sm font-medium transition ${
                      selected
                        ? 'border-[#5B6B3F] bg-[#5B6B3F] text-white'
                        : 'border-[#EBE8E0] bg-white text-[#2A2A28]'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Horaires des services</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            ['dine_in', 'Sur place'],
            ['delivery', 'Livraison'],
            ['takeaway', 'Emporter'],
          ].map(([prefix, label]) => (
            <div key={prefix} className="rounded-xl border border-[#EBE8E0] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="font-medium text-[#2A2A28]">{label}</h3>
                <Switch
                  checked={!!settings[`${prefix}_enabled`]}
                  onCheckedChange={checked => setSettings({ ...settings, [`${prefix}_enabled`]: checked })}
                />
              </div>
              <div className="grid gap-3">
                <div>
                  <Label className="text-sm">Ouverture</Label>
                  <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings[`${prefix}_hours_start`] || '11:30'} onChange={e => setSettings({ ...settings, [`${prefix}_hours_start`]: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Fermeture</Label>
                  <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings[`${prefix}_hours_end`] || '22:30'} onChange={e => setSettings({ ...settings, [`${prefix}_hours_end`]: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Pause apres-midi debut</Label>
                  <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings[`${prefix}_break_start`] || '15:30'} onChange={e => setSettings({ ...settings, [`${prefix}_break_start`]: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Pause apres-midi fin</Label>
                  <Input type="time" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings[`${prefix}_break_end`] || '19:00'} onChange={e => setSettings({ ...settings, [`${prefix}_break_end`]: e.target.value })} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold text-[#2A2A28]">Imprimante Epson TM-M30II</h2>
            <p className="text-sm text-[#7A7A75] mt-1">Configure l'imprimante du comptoir. L'impression passera par un agent local installe sur l'ordinateur du restaurant.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]"
            onClick={() => setSettings({ ...settings, printers: [...(settings.printers || []), createPrinterDraft()] })}
          >
            Ajouter une imprimante
          </Button>
        </div>
        <div className="space-y-4">
          {(settings.printers || []).map((printer, printerIndex) => (
            <div key={printer.id || printerIndex} className="rounded-xl border border-[#EBE8E0] p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm">Nom de l'imprimante</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={printer.name || ''} onChange={e => updatePrinter(printerIndex, { name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm">Type de connexion</Label>
                  <select
                    className="mt-1 h-10 w-full rounded-xl border border-[#EBE8E0] bg-white px-3 text-sm"
                    value={printer.type || 'bluetooth'}
                    onChange={e => updatePrinter(printerIndex, { type: e.target.value })}
                  >
                    <option value="bluetooth">Bluetooth</option>
                    <option value="network">Reseau (Ethernet / Wi-Fi)</option>
                    <option value="usb">USB</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Adresse Bluetooth</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={printer.connection?.bluetooth_address || ''} onChange={e => updatePrinterConnection(printerIndex, 'bluetooth_address', e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
                </div>
                <div>
                  <Label className="text-sm">IP imprimante</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={printer.connection?.host || ''} onChange={e => updatePrinterConnection(printerIndex, 'host', e.target.value)} placeholder="192.168.1.120" />
                </div>
                <div>
                  <Label className="text-sm">Port reseau</Label>
                  <Input type="number" className="mt-1 rounded-xl border-[#EBE8E0]" value={printer.connection?.port || 9100} onChange={e => updatePrinterConnection(printerIndex, 'port', e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm">Secret agent local</Label>
                  <Input className="mt-1 rounded-xl border-[#EBE8E0]" value={printer.secret || ''} onChange={e => updatePrinter(printerIndex, { secret: e.target.value })} placeholder="Colle le meme secret dans l'agent local" />
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="flex items-center justify-between rounded-xl border border-[#EBE8E0] p-3">
                  <div>
                    <Label className="text-sm font-semibold text-[#2A2A28]">Imprimante active</Label>
                    <p className="text-xs text-[#7A7A75] mt-1">Active la file d'impression</p>
                  </div>
                  <Switch checked={printer.active !== false} onCheckedChange={checked => updatePrinter(printerIndex, { active: checked })} />
                </div>
                <div className="flex items-center justify-between rounded-xl border border-[#EBE8E0] p-3">
                  <div>
                    <Label className="text-sm font-semibold text-[#2A2A28]">Auto-impression</Label>
                    <p className="text-xs text-[#7A7A75] mt-1">Imprime a la confirmation admin</p>
                  </div>
                  <Switch checked={printer.auto_print_on_confirm !== false} onCheckedChange={checked => updatePrinter(printerIndex, { auto_print_on_confirm: checked })} />
                </div>
                <div className="rounded-xl border border-[#EBE8E0] p-3">
                  <Label className="text-sm font-semibold text-[#2A2A28]">Statut agent</Label>
                  <p className={`mt-1 text-sm font-medium ${printer.status === 'online' ? 'text-[#5B6B3F]' : 'text-[#8C3A3A]'}`}>
                    {printer.status === 'online' ? 'En ligne' : 'Hors ligne'}
                  </p>
                  <p className="text-xs text-[#7A7A75] mt-1">
                    {printer.last_seen_at ? `Dernier signal: ${new Date(printer.last_seen_at).toLocaleString('fr-FR')}` : "Aucun signal recu"}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A]"
                  onClick={() => setSettings({ ...settings, printers: (settings.printers || []).filter((_, index) => index !== printerIndex) })}
                >
                  Supprimer
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Fermeture exceptionnelle</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Activer la fermeture exceptionnelle</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Le bandeau d'information s'affiche sur le site. Les services se reglent ci-dessous.</p>
            </div>
            <Switch
              checked={!!settings.exceptional_closure_enabled}
              onCheckedChange={checked => setSettings({ ...settings, exceptional_closure_enabled: checked })}
              data-testid="settings-closure-enabled"
            />
          </div>
          <div>
            <Label className="text-sm">Titre</Label>
            <Input
              className="mt-1 rounded-xl border-[#EBE8E0]"
              value={settings.exceptional_closure_title || ''}
              onChange={e => setSettings({ ...settings, exceptional_closure_title: e.target.value })}
              data-testid="settings-closure-title"
            />
          </div>
          <div>
            <Label className="text-sm">Message</Label>
            <Textarea
              className="mt-1 rounded-xl border-[#EBE8E0]"
              value={settings.exceptional_closure_message || ''}
              onChange={e => setSettings({ ...settings, exceptional_closure_message: e.target.value })}
              data-testid="settings-closure-message"
            />
          </div>
          <div>
            <Label className="text-sm">Date et heure de fin automatique</Label>
            <Input className="mt-1 rounded-xl border-[#EBE8E0]" type="datetime-local" value={settings.exceptional_closure_end_at || ''} onChange={e => setSettings({ ...settings, exceptional_closure_end_at: e.target.value })} />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#EBE8E0] p-3">
            <div>
              <Label className="text-sm font-semibold text-[#2A2A28]">Autoriser les commandes pendant la fermeture</Label>
              <p className="text-xs text-[#7A7A75] mt-1">Les reservations et devis se gerent avec les interrupteurs ci-dessous</p>
            </div>
            <Switch checked={!!settings.exceptional_closure_allow_orders} onCheckedChange={checked => setSettings({ ...settings, exceptional_closure_allow_orders: checked })} />
          </div>
          <div className="border-t border-[#EBE8E0] pt-4">
            <p className="text-sm font-semibold text-[#2A2A28] mb-3">Disponibilite des demandes</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E0] p-3">
                <div>
                  <Label className="text-sm font-semibold text-[#2A2A28]">Reservations</Label>
                  <p className="text-xs text-[#7A7A75] mt-1">Autoriser les demandes de table</p>
                </div>
                <Switch
                  checked={settings.reservations_enabled !== false}
                  onCheckedChange={checked => setSettings({ ...settings, reservations_enabled: checked })}
                  data-testid="settings-reservations-enabled"
                />
              </div>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[#EBE8E0] p-3">
                <div>
                  <Label className="text-sm font-semibold text-[#2A2A28]">Devis</Label>
                  <p className="text-xs text-[#7A7A75] mt-1">Autoriser les demandes de devis</p>
                </div>
                <Switch
                  checked={settings.quotes_enabled !== false}
                  onCheckedChange={checked => setSettings({ ...settings, quotes_enabled: checked })}
                  data-testid="settings-quotes-enabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">TVA</h2>
        <div className="max-w-sm">
          <Label className="text-sm">TVA par defaut (%)</Label>
          <Input type="number" step="0.01" className="mt-1 rounded-xl border-[#EBE8E0]" value={settings.default_vat_rate || 10} onChange={e => setSettings({ ...settings, default_vat_rate: e.target.value })} />
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-[#2A2A28]">Offres</h2>
          <Button type="button" variant="outline" className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]" onClick={() => setSettings({ ...settings, offers: [...(settings.offers || []), emptyOffer()] })}>Ajouter une offre</Button>
        </div>
        <div className="space-y-4">
          {(settings.offers || []).map((offer, index) => (
            <div key={offer.id || index} className="rounded-xl border border-[#EBE8E0] p-4">
              <div className="grid gap-3">
                <Input className="rounded-xl border-[#EBE8E0]" value={offer.title || ''} placeholder="Titre" onChange={e => updateOffer(index, { title: e.target.value })} />
                <Textarea className="rounded-xl border-[#EBE8E0]" value={offer.description || ''} placeholder="Description" onChange={e => updateOffer(index, { description: e.target.value })} />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <Label className="text-sm font-semibold text-[#2A2A28]">Articles/Formules qui declenchent l'offre</Label>
                  <p className="text-xs text-[#7A7A75] mt-1">Tous les plats et toutes les formules sont disponibles dans cette liste.</p>
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-[#EBE8E0] p-2 space-y-2">
                    {availableOfferItems.map(dish => {
                      const selected = (offer.trigger_dish_ids || []).includes(dish.id);
                      const quantity = Number((offer.trigger_item_quantities || {})[dish.id] || 0);
                      return (
                        <div
                          key={`trigger-${offer.id}-${dish.id}`}
                          className={`w-full rounded-xl border px-3 py-2 text-left text-sm ${
                            selected ? 'border-[#5B6B3F] bg-[#5B6B3F]/8 text-[#2A2A28]' : 'border-[#EBE8E0] bg-white text-[#7A7A75]'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => toggleOfferDish(index, 'trigger_dish_ids', dish.id)}
                              className="min-w-0 flex-1 text-left"
                            >
                              {getDishLabel(dish.id)}
                            </button>
                            {selected && (
                              <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setOfferDishQuantity(index, 'trigger_item_quantities', 'trigger_dish_ids', dish.id, quantity - 1)} className="h-8 w-8 rounded-full border border-[#5B6B3F]/20 text-[#5B6B3F]">-</button>
                                <span className="min-w-8 text-center font-semibold text-[#5B6B3F]">{quantity}</span>
                                <button type="button" onClick={() => setOfferDishQuantity(index, 'trigger_item_quantities', 'trigger_dish_ids', dish.id, quantity + 1)} className="h-8 w-8 rounded-full border border-[#5B6B3F]/20 bg-[#5B6B3F] text-white">+</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#2A2A28]">Articles offerts possibles</Label>
                  <p className="text-xs text-[#7A7A75] mt-1">Ex: le 6eme sandwich offert, ou un dessert offert</p>
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-[#EBE8E0] p-2 space-y-2">
                    {availableOfferItems.map(dish => {
                      const selected = (offer.reward_dish_ids || []).includes(dish.id);
                      const quantity = Number((offer.reward_item_quantities || {})[dish.id] || 0);
                      return (
                        <div
                          key={`reward-${offer.id}-${dish.id}`}
                          className={`w-full rounded-xl border px-3 py-2 text-left text-sm ${
                            selected ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#2A2A28]' : 'border-[#EBE8E0] bg-white text-[#7A7A75]'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => toggleOfferDish(index, 'reward_dish_ids', dish.id)}
                              className="min-w-0 flex-1 text-left"
                            >
                              {getDishLabel(dish.id)}
                            </button>
                            {selected && (
                              <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setOfferDishQuantity(index, 'reward_item_quantities', 'reward_dish_ids', dish.id, quantity - 1)} className="h-8 w-8 rounded-full border border-[#D4AF37]/20 text-[#7A5B12]">-</button>
                                <span className="min-w-8 text-center font-semibold text-[#D4A840]">{quantity}</span>
                                <button type="button" onClick={() => setOfferDishQuantity(index, 'reward_item_quantities', 'reward_dish_ids', dish.id, quantity + 1)} className="h-8 w-8 rounded-full border border-[#D4AF37]/20 bg-[#D4A840] text-white">+</button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={offer.active !== false} onCheckedChange={checked => updateOffer(index, { active: checked })} />
                  <span className="text-sm text-[#2A2A28]">Active</span>
                </div>
                <Button type="button" variant="outline" className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => setSettings({ ...settings, offers: settings.offers.filter((_, itemIndex) => itemIndex !== index) })}>Supprimer</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Map Preview */}
      <div className="mt-6 bg-white rounded-xl border border-[#EBE8E0] p-5">
        <h2 className="font-heading text-lg font-semibold text-[#2A2A28] mb-4">Zone de livraison</h2>
        <div ref={mapRef} className="w-full h-[400px] rounded-xl overflow-hidden border border-[#EBE8E0]" data-testid="settings-map" />
        <p className="text-xs text-[#7A7A75] mt-2">Le cercle vert represente la zone de livraison ({settings.max_delivery_distance_km} km)</p>
      </div>

      <div className="mt-6">
        <Button onClick={save} disabled={loading} className="rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white px-8 h-11" data-testid="settings-save">
          <Save className="w-4 h-4 mr-2" /> {loading ? 'Enregistrement...' : 'Enregistrer les parametres'}
        </Button>
      </div>
    </div>
  );
}
